import { useState } from 'react';
import { PageShell } from '../PageShell/index';
import { SectionHeading } from '../../shared/components/SectionHeading';
import { Ruler, Scale } from 'lucide-react';

export function PigWeightCalculator() {
  const [unit, setUnit] = useState('cm');
  const [bcs, setBcs] = useState('3');
  const [girth, setGirth] = useState('');
  const [length, setLength] = useState('');

  const KG_PER_LB = 0.45359237;
  const CM_PER_IN = 2.54;
  const DIVISOR = 400;

  const bcsStatus = (bcsVal: any) => {
    const val = Number(bcsVal);
    if (val <= 1) return { label: "Very thin", note: "Increase nutrition review and check animal health." };
    if (val === 2) return { label: "Thin", note: "Feed balance may need improvement." };
    if (val === 3) return { label: "Ideal", note: "Maintain balanced feeding and regular monitoring." };
    if (val === 4) return { label: "Fat", note: "Review energy intake and activity." };
    return { label: "Very fat", note: "Over-conditioned. Feed control may be needed." };
  };

  const calculateWeight = (u: any, g: any, l: any) => {
    const numGirth = Number(g);
    const numLength = Number(l);
    if (!numGirth || !numLength || numGirth <= 0 || numLength <= 0) return null;
    
    if (u === "in") {
      const lb = (numGirth * numGirth * numLength) / DIVISOR;
      return { lb, kg: lb * KG_PER_LB };
    }
    const girthIn = numGirth / CM_PER_IN;
    const lengthIn = numLength / CM_PER_IN;
    const lb = (girthIn * girthIn * lengthIn) / DIVISOR;
    return { lb, kg: lb * KG_PER_LB };
  };

  const formatWeight = (value: any) => {
    if (!isFinite(value)) return "--";
    if (value >= 100) return Math.round(value).toLocaleString();
    return value.toFixed(1);
  };

  const weightResult = calculateWeight(unit, girth, length);
  const status = bcsStatus(bcs);

  return (
    <PageShell title="Pig Weight Calculator" eyebrow="Calculator" description="Estimate ideal body weight based on heart girth and body length" path="/calculators/pig" breadcrumbs={[{ label: 'Farmer Corner', href: '/farmer-corner' }, { label: 'Pig Calculator' }]}>
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-start">
          
          <div className="flex flex-col gap-8">
            <div>
              <SectionHeading title="Pig Ideal Body Weight Calculator">
                <p className="mt-4 text-lg text-charcoal/80">
                  Estimate pig body weight using heart girth and body length. This simple tool helps with feed planning and body condition monitoring.
                </p>
              </SectionHeading>
            </div>

            <div className="rounded-3xl bg-white p-6 shadow-soft ring-1 ring-forest/5 sm:p-8">
              <form className="grid gap-6" onSubmit={(e) => e.preventDefault()}>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="unit" className="block text-sm font-bold text-ink">Measurement Unit</label>
                    <select id="unit" value={unit} onChange={(e) => setUnit(e.target.value)} className="w-full rounded-xl border border-forest/20 bg-white px-4 py-3 text-ink focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest">
                      <option value="cm">Centimetres (cm)</option>
                      <option value="in">Inches (in)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="bcs" className="block text-sm font-bold text-ink">Body Condition Score</label>
                    <select id="bcs" value={bcs} onChange={(e) => setBcs(e.target.value)} className="w-full rounded-xl border border-forest/20 bg-white px-4 py-3 text-ink focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest">
                        <option value="1">BCS 1 - Very thin</option>
                        <option value="2">BCS 2 - Thin</option>
                        <option value="3">BCS 3 - Ideal</option>
                        <option value="4">BCS 4 - Fat</option>
                        <option value="5">BCS 5 - Very fat</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="girth" className="block text-sm font-bold text-ink">Heart Girth ({unit})</label>
                    <input id="girth" type="number" min="1" step="0.1" placeholder="Example: 120" value={girth} onChange={(e) => setGirth(e.target.value)} className="w-full rounded-xl border border-forest/20 bg-white px-4 py-3 text-ink focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest" />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="length" className="block text-sm font-bold text-ink">Body Length ({unit})</label>
                    <input id="length" type="number" min="1" step="0.1" placeholder="Example: 105" value={length} onChange={(e) => setLength(e.target.value)} className="w-full rounded-xl border border-forest/20 bg-white px-4 py-3 text-ink focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 pt-2">
                  <button type="button" onClick={() => { setUnit("cm"); setBcs("3"); setGirth(""); setLength(""); }} className="rounded-xl bg-forest px-8 py-3 font-bold text-white transition hover:bg-forest/90">Reset</button>
                </div>
              </form>
            </div>
            
            <div className="rounded-3xl overflow-hidden ring-1 ring-forest/10 bg-white">
              <img src="/images/farmer-corner/pig-measurement.webp" alt="Pig measurement guide" className="w-full h-auto object-contain" />
            </div>
          </div>

          <div className="sticky top-28 flex flex-col gap-6">
            <div className="rounded-[1.4rem] bg-gradient-to-br from-forest via-forest to-[#0b381a] p-8 text-white shadow-xl">
              <div className="flex items-center gap-3 mb-6 opacity-90">
                <Scale className="w-5 h-5" />
                <span className="text-sm font-bold uppercase tracking-wider">Estimated Weight</span>
              </div>
              <div className="mb-8">
                <span className="text-6xl font-black tracking-tight">{weightResult ? formatWeight(weightResult.kg) : "--"}</span>
                <span className="ml-2 text-2xl font-bold opacity-80">kg</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-8 border-t border-white/20 pt-6">
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider opacity-70 mb-1">Weight in Lbs</span>
                  <strong className="text-xl">{weightResult ? formatWeight(weightResult.lb) : "--"}</strong>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider opacity-70 mb-1">BCS Status</span>
                  <strong className="text-xl">{status.label}</strong>
                </div>
              </div>
              <div className="rounded-xl bg-white/10 p-5 backdrop-blur-sm">
                <p className="text-sm font-medium leading-relaxed">
                  {weightResult ? status.note + ' Ideal target is generally around BCS 3.' : 'Enter measurements to calculate. Ideal target is generally around BCS 3.'}
                </p>
              </div>
            </div>

            <div className="rounded-[1.4rem] bg-tint p-8">
              <h3 className="font-black text-ink mb-6 flex items-center gap-2"><Ruler className="w-5 h-5 text-forest" /> How to Measure</h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-forest text-sm font-bold text-white">1</div>
                  <div>
                    <h4 className="font-bold text-ink">Heart girth</h4>
                    <p className="mt-1 text-sm text-charcoal">Wrap the tape around the chest just behind the front legs. Keep the tape firm but not tight.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-forest text-sm font-bold text-white">2</div>
                  <div>
                    <h4 className="font-bold text-ink">Body length</h4>
                    <p className="mt-1 text-sm text-charcoal">Measure the body length in a straight line according to the animal type. Avoid bending the tape.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-forest text-sm font-bold text-white">3</div>
                  <div>
                    <h4 className="font-bold text-ink">BCS check</h4>
                    <p className="mt-1 text-sm text-charcoal">Use BCS to understand whether the animal is thin, ideal, or over-conditioned before changing feeding.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-charcoal/60 leading-relaxed px-2">
              Note: This calculator gives an estimated weight, not an exact scale weight. For medicine dosage, disease, pregnancy concerns, or critical feeding decisions, confirm with a veterinarian or livestock expert. 
              <br/><br/>
              Formula used: <code>Weight (lb) = Heart Girth² × Body Length ÷ 400</code>
            </p>
          </div>
          
        </div>
      </section>
    </PageShell>
  );
}
